import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  Stack,
  Divider,
  Fade,
  Grow,
  Slide,
  styled,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  LocationOn,
  AccessTime,
  CurrencyRupee,
} from "@mui/icons-material";

import useAxios from "../useAxios";
import { useDispatch } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { addToCart } from "../redux/slices/cartSlice";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { useNavigate } from "react-router-dom";
import { se } from "date-fns/locale";
// Red-themed styled components
const RedGradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #FF416C 0%, #FF4B2B 100%)",
  color: "white",
  fontWeight: 700,
  padding: "14px 28px",
  borderRadius: "12px",
  boxShadow: "0 4px 15px rgba(255, 65, 108, 0.3)",
  textTransform: "none",
  fontSize: "1rem",
  "&:hover": {
    background: "linear-gradient(45deg, #FF416C 30%, #FF4B2B 90%)",
    boxShadow: "0 6px 20px rgba(255, 75, 43, 0.4)",
  },
  transition: "all 0.3s ease",
}));

const RedGlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(12px)",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0 8px 32px rgba(255, 38, 38, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
}));

const DistanceChip = styled(Chip)(({ theme }) => ({
  background: "rgba(255, 235, 238, 0.9)",
  color: "#D32F2F",
  fontWeight: 600,
  marginRight: theme.spacing(1),
  "& .MuiChip-icon": {
    color: "#FF5252",
  },
}));

// Pricing logic parser
const parsePricingData = (products) => {
  const cityBasePrices = {};
  const durationMultipliers = {};

  products.price.forEach((item) => {
    const key = Object.keys(item)[0];
    const value = item[key];

    if (isNaN(value)) {
      durationMultipliers[key] = value;
    } else {
      cityBasePrices[key.toLowerCase()] = parseInt(value);
    }
  });

  return { cityBasePrices, durationMultipliers };
};
const PricingDetails = ({
  products,
  selectCity,
  onCityChange,
  userDetails,
  disablePricing,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCity, setSelectedCity] = useState(selectCity || "");
  const [eventTiming, setEventTiming] = useState("");
  const [selectedHours, setSelectedHours] = useState("1");
  const [selectedDays, setSelectedDays] = useState("4");
  const [selectedDaysLabel, setSelectedDaysLabel] = useState("");
  const [isVirtualGame, setIsVirtualGame] = useState(false);
  const [selectedUserRange, setSelectedUserRange] = useState("");
  const [selectedCustomRange, setSelectedCustomRange] = useState(0);
  const [virtualDurations, setVirtualDurations] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [showCart2, setShowCart2] = useState(false);
  const axiosData = useAxios();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // city status per category
  const [cityStatus, setCityStatus] = useState({});
  const [categories, setCategories] = useState([]);
  const { cityBasePrices, extraPerHour, hourRules, dayMultipliers } =
    useMemo(() => {
      const cityBasePrices = {};
      let extraPerHour = 0;
      const hourRules = []; // [{ min: 3, max: 4, label: "As Usual" }]
      const dayMultipliers = { 1: 1 }; // { "2": 2, "1.5": 1.5 }
      const priceVal =
        selectedCustomRange === 0
          ? products.price
          : products[`price${selectedCustomRange}`];
      priceVal.forEach((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        parseInt(value);
        // Base city prices
        if (!isNaN(value) && /^[a-zA-Z\s]+$/.test(key)) {
          cityBasePrices[key.toLowerCase().replace(/\s+/g, "")] =
            parseInt(value);
        }

        // Extra per hour
        if (key.toLowerCase() === "extra per hour") {
          extraPerHour = parseInt(value); // from { "Extra Per Hour": "2000" }
        }

        // Hour rules like "3-4 Hrs": "As Usual"
        if (key.match(/^\d+-\d+ Hrs$/)) {
          const [min, max] = key.split(" ")[0].split("-").map(Number);
          hourRules.push({ min, max, label: value });
        }

        // Day multipliers like "2 Days": "2X"
        if (key.match(/^\d+(\.\d+)? Days$/) && value.endsWith("X")) {
          const dayCount = parseFloat(key.split(" ")[0]); // ?? convert to number
          const multiplier = parseFloat(value.replace("X", ""));
          dayMultipliers[dayCount] = multiplier;
        }
      });

      return { cityBasePrices, extraPerHour, hourRules, dayMultipliers };
    }, [products, selectedCustomRange]);
  /* Get cities list from api*/
  const priceFor = isVirtualGame
    ? `This experience is for ${selectedUserRange}${
        selectedDaysLabel ? `, lasting ${selectedDaysLabel}` : ""
      }${
        selectedHours
          ? `, with ${selectedHours} hour${
              selectedHours > 1 ? "s" : ""
            } per day.`
          : ""
      }`
    : `Happening in ${
        selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
      }${
        selectedDays
          ? ` for ${selectedDays} day${selectedDays > 1 ? "s" : ""}`
          : ""
      }${
        selectedHours
          ? `, with ${selectedHours} hour${
              selectedHours > 1 ? "s" : ""
            } per day.`
          : ""
      }`;

  const fetchCities = async () => {
    await axiosData
      .get(`getGeneralDataByName/cities`)
      .then((data) => {
        const dataCities = data.data.generalData;
        setCityOptions(dataCities.datasList);
      })
      .catch((error) => {
        console.log("Error", error);
      });
  };
  const handleAddToCart = async () => {
    dispatch(
      addToCart({
        product_id: products._id,
        name: products.produt_name,
        quantity,
        category: products.productCategory,
        image: products.image_local_url,
        price: price,
        priceFor: priceFor,
      })
    );
    try {
      const storedAuth = localStorage.getItem("auth");
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        await axiosData.post("createCartData", {
          user_id: userDetails._id,
          email: userDetails.email,
          mobile_number: userDetails.mobile_number,
          items: [
            {
              product_id: products._id,
              name: products.produt_name,
              quantity,
              category: products.productCategory,
              image: products.image_local_url,
              price: price,
              priceFor: priceFor,
            },
          ],
        });
      }
    } catch (err) {
      console.error("Error saving cart to database", err);
    }
    setOpen(true);
  };
  useEffect(() => {
    setSelectedCity(selectCity); // Keep in sync if parent updates it
  }, [selectCity]);
  useEffect(() => {
    setCategories([]);
    setSelectedDays("1");
    setSelectedHours("4");
    fetchCities();
    fetchCategory();
  }, []);
  /* Get all categories list from api */
  const fetchCategory = async () => {
    try {
      const data = await axiosData.get(`getAllProductCategory`);
      const productCategories = data.data.productCAtegory || [];
      setCategories(data.data.productCAtegory);

      // initialize city status for each category
      const initStatus = {};
      productCategories.forEach((cat) => {
        initStatus[cat._id] = cat.status || {}; // from API
      });
      setCityStatus(initStatus);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setLoading(false);
    }
  };
  const getFinalStatus = () => {
    const cityKey =
      selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1);

    // 1. product status
    const statusForCity = products?.status?.[cityKey];
    const cityStatus2 = statusForCity?.toLowerCase();

    // 2. category status
    const catGet = categories?.find(
      (cat) => cat._id === products.productCategoryID
    );
    const statusForCityCat = catGet?.status?.[cityKey];
    const cityStatus2Cat = statusForCityCat?.toLowerCase();

    // 3. final result
    return cityStatus2 === "off" || cityStatus2Cat === "off" ? "off" : "on";
  };

  // const calculatePrice = () => {
  //   if (!selectedCity) return 0;

  //   const base = cityBasePrices[selectedCity.toLowerCase()];
  //   if (!base) return 0;

  //   let extraMultiplier  = 1;

  //   // ?? Extra charge after 4 hours
  //  if (selectedHours > 4) {
  //   const additionalHours = selectedHours - 4;
  //   const additionalCost = additionalHours * extraPerHour;
  //   extraMultiplier += additionalCost / base;
  // }

  //   // ?? Day multiplier
  //   let dayMultiplier = (dayMultipliers[selectedDays] ?? selectedDays) || 1;
  //   // if (selectedDays) {
  //   //   const multiplier = dayMultipliers[String(selectedDays)];
  //   //   if (multiplier) {
  //   //     dayMultiplier = multiplier;
  //   //   }
  //   // }

  //   // ?? Total cost = (base � day multiplier) + extra cost � timing factor
  //   const total = base * extraMultiplier * dayMultiplier;
  //   return Math.round(total);
  // };
  // Updated calculatePrice logic inside your PricingDetails component
  const calculatePrice = () => {
    const selectedPriceArray =
      selectedCustomRange === 0
        ? products.price
        : products[`price${selectedCustomRange}`];
    // if (!selectedCity && !selectedUserRange) return 0;
    const base = cityBasePrices[selectedCity?.toLowerCase()];
    // Inside calculatePrice
    if (isVirtualGame) {
      if (!selectedUserRange || !selectedDaysLabel) return 0;

      let virtualBase = 0;
      let virtualDayMultiplier = 1;
      const productsPrice =
        selectedCustomRange === 0
          ? products.price
          : products[`price${selectedCustomRange}`];
      // Step 1: Get the base price based on selected user range
      selectedPriceArray.forEach((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];

        if (
          key.trim().toLowerCase() === selectedUserRange.trim().toLowerCase()
        ) {
          virtualBase = parseInt(value);
        }
      });

      // Step 2: Handle days multiplier
      const selectedDaysGet = parseInt(selectedDaysLabel); // "4 Days" => 4

      selectedPriceArray.forEach((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];

        // Case: "7 Days" => "1 Week"
        if (selectedDaysGet === 7 && key === "1 Week") {
          if (value.includes("Add")) {
            const percentage = parseFloat(
              value.replace("Add", "").replace("%", "")
            );
            virtualDayMultiplier += percentage / 100;
          } else if (value.includes("X")) {
            virtualDayMultiplier = parseFloat(value.replace("X", ""));
          }
        }

        // Case: "30 Days" => "1 Month"
        else if (selectedDaysGet === 30 && key === "1 Month") {
          if (value.includes("Add")) {
            const percentage = parseFloat(
              value.replace("Add", "").replace("%", "")
            );
            virtualDayMultiplier += percentage / 100;
          } else if (value.includes("X")) {
            virtualDayMultiplier = parseFloat(value.replace("X", ""));
          }
        }

        // Case: direct match like "2 Days", "3 Days"
        else {
          const match = key.match(/^(\d+)\s+Days$/);
          if (match && parseInt(match[1]) === selectedDaysGet) {
            if (value.includes("Add")) {
              const percentage = parseFloat(
                value.replace("Add", "").replace("%", "")
              );
              virtualDayMultiplier += percentage / 100;
            } else if (value.includes("X")) {
              virtualDayMultiplier = parseFloat(value.replace("X", ""));
            }
          }
        }
      });

      return Math.round(virtualBase * virtualDayMultiplier);
    }

    let extraMultiplier = 1;

    // ?? Extra charge after 4 hours
    if (selectedHours > 4) {
      const additionalHours = selectedHours - 4;
      const additionalCost = additionalHours * extraPerHour;
      extraMultiplier += additionalCost / base;
    }

    // ?? Determine if it's a virtual game (no city-based pricing)
    const isVirtual =
      !base &&
      selectedPriceArray.some((item) => Object.keys(item)[0].includes("Users"));

    let virtualBase = 0;
    let virtualDayMultiplier = 1;

    if (isVirtual) {
      selectedPriceArray.forEach((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        // Base virtual pricing
        if (key.includes("Users")) {
          if (key === selectedCity) virtualBase = parseInt(value);
        }

        // Duration-based logic for virtual pricing
        if (selectedDays && key.includes("Days") && value.includes("Add")) {
          const percentage = parseFloat(
            value.replace("Add", "").replace("%", "")
          );
          if (key.startsWith(selectedDays + " ")) {
            virtualDayMultiplier += percentage / 100;
          }
        } else if (
          selectedDays &&
          key.includes("Week") &&
          key.startsWith("1") &&
          value.includes("X")
        ) {
          virtualDayMultiplier = parseFloat(value.replace("X", ""));
        } else if (
          selectedDays &&
          key.includes("Month") &&
          value.includes("X")
        ) {
          virtualDayMultiplier = parseFloat(value.replace("X", ""));
        }
      });

      const virtualTotal = virtualBase * virtualDayMultiplier;
      return Math.round(virtualTotal);
    }

    // ?? Day multiplier
    let dayMultiplier =
      (dayMultipliers[selectedDays] ?? parseFloat(selectedDays)) || 1;

    // ?? Total cost = (base � day multiplier) + extra cost � timing factor
    const total = base * dayMultiplier * extraMultiplier;
    return Math.round(total);
  };
  useEffect(() => {
    setSelectedCity(selectCity);
    const isVirtual = (
      selectedCustomRange === 0
        ? products.price
        : products[`price${selectedCustomRange}`]
    ).some((item) => Object.keys(item)[0].toLowerCase().includes("user"));
    setIsVirtualGame(isVirtual);

    if (isVirtual) {
      const extractedDurations = (
        selectedCustomRange === 0
          ? products.price
          : products[`price${selectedCustomRange}`]
      )
        .filter((item) => {
          const key = Object.keys(item)[0];
          return (
            key.toLowerCase().includes("days") ||
            key.toLowerCase().includes("week") ||
            key.toLowerCase().includes("month")
          );
        })
        .map((item) => Object.keys(item)[0]);

      const fullDurationOptions = [
        ...Array.from({ length: 30 }, (_, i) => `${i + 1} Days`),
      ];

      // Filter out 8�14 and 16�29 days
      const filteredDurations = fullDurationOptions.filter((item) => {
        const day = parseInt(item);
        return (
          day < 8 || day === 15 || day === 30 // Keep 1�7, 15, and 30
        );
      });

      setVirtualDurations(filteredDurations);
    }
  }, [selectCity, products, selectedCustomRange]);

  // useEffect(() => {
  //   setSelectedCity(selectCity); // sync
  //   const isVirtual = products.price.some((item) =>
  //     Object.keys(item)[0].toLowerCase().includes("user")
  //   );
  //   setIsVirtualGame(isVirtual);

  //   // Extract durations for virtual dropdown
  //   if (isVirtual) {
  //     const durations = products.price
  //       .filter((item) => {
  //         const key = Object.keys(item)[0];
  //         return (
  //           key.toLowerCase().includes("days") ||
  //           key.toLowerCase().includes("week") ||
  //           key.toLowerCase().includes("month")
  //         );
  //       })
  //       .map((item) => Object.keys(item)[0]);
  //     setVirtualDurations(durations);
  //   }
  // }, [selectCity, products]);
  const price = calculatePrice();
  //const availableCities = Object.keys(cityBasePrices);
  const hourOptions = Array.from({ length: 24 }, (_, i) => i + 1);
  const dayOptions = Array.from({ length: 30 }, (_, i) => i + 1);
  const availableCities = useMemo(() => {
    const cities = Object.keys(cityBasePrices);
    const priceVal2 =
      selectedCustomRange === 0
        ? products.price
        : products[`price${selectedCustomRange}`];
    const virtualOptions = priceVal2
      .filter((item) => Object.keys(item)[0].toLowerCase().includes("users"))
      .map((item) => Object.keys(item)[0]);
    return [...cities, ...virtualOptions];
  }, [cityBasePrices, selectedCustomRange, products]);
  return (
    <Box sx={{ width:isMobile?"100%":290 }}>
      <RedGradientButton
        fullWidth
        onClick={() => setShowDropdown(!showDropdown)}
        endIcon={showDropdown ? <ExpandLess /> : <ExpandMore />}
        sx={{ mb: 2 }}
      >
        Get Pricing Details
      </RedGradientButton>
      {products.produt_name === "Fridge Magnet Photo Booth" && (
        <Typography sx={{ fontWeight: 800, color: "green", paddingBottom: 1 }}>
          Note: This price is for 100 units. Each additional unit costs
          <CurrencyRupee sx={{ fontSize: "15px", textAlign: "center" }} />
          {"350"}.
        </Typography>
      )}
      {products.produt_name === "Bobble Head Photo Booth" && (
        <Typography sx={{ fontWeight: 800, color: "green", paddingBottom: 1 }}>
          Note: This price is for 100 units. Each additional unit costs
          <CurrencyRupee sx={{ fontSize: "15px", textAlign: "center" }} />
          {"300"}.
        </Typography>
      )}
      {products.produt_name === "Lenticular Photo Booth" && (
        <Typography sx={{ fontWeight: 800, color: "green", paddingBottom: 1 }}>
          Note: This price is for 100 units. Each additional unit costs
          <CurrencyRupee sx={{ fontSize: "15px", textAlign: "center" }} />
          {"400"}.
        </Typography>
      )}
      {products.produt_name === "BagTag Photo Booth" && (
        <Typography sx={{ fontWeight: 800, color: "green", paddingBottom: 1 }}>
          Note: This price is for 100 cards. Each additional card costs
          <CurrencyRupee sx={{ fontSize: "15px", textAlign: "center" }} />
          {
            (selectedCustomRange === 0
              ? products.price
              : products[`price${selectedCustomRange}`]
            ).find((p) => p["Extra per Card"])?.["Extra per Card"]
          }
        </Typography>
      )}
      {products.produt_name === "Flip Book Photo Booth" && (
        <Typography sx={{ fontWeight: 800, color: "green", paddingBottom: 1 }}>
          Note: This price is for 100 books. Each additional book costs
          <CurrencyRupee sx={{ fontSize: "15px", textAlign: "center" }} />
          {
            (selectedCustomRange === 0
              ? products.price
              : products[`price${selectedCustomRange}`]
            ).find((p) => p["Extra per Book"])?.["Extra per Book"]
          }
          .
        </Typography>
      )}
      <Slide direction="down" in={showDropdown} mountOnEnter unmountOnExit>
        <RedGlassCard elevation={0}>
          <Stack spacing={3}>
            {/* <Typography
              variant="body2"
              color="#D32F2F"
              sx={{ fontStyle: "italic" }}
            >
              Pricing varies based on event timing and distance
            </Typography> */}
            {products?.Customization.length > 0 && (
              <Fade in={true}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                    Select Custom Range
                  </InputLabel>
                  <Select
                    value={selectedCustomRange}
                    onChange={(e) => {
                      const newRange = e.target.value;
                      setSelectedCustomRange(newRange);
                      setSelectedCity(selectCity || ""); // reset city to default
                      setSelectedUserRange(""); // reset virtual user range
                      setSelectedDays("1"); // reset days
                      setSelectedDaysLabel(""); // reset duration label
                      setSelectedHours("4"); // reset hours
                      onCityChange?.(selectCity || "");
                    }}
                    label="Select Custom Range"
                    MenuProps={{
                      disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                    }}
                  >
                    {products.Customization.map((city, index) => (
                      <MenuItem key={city} value={index}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Fade>
            )}
            {/* City Selection */}
            <Grow in={true}>
              {isVirtualGame ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                    Select User Range
                  </InputLabel>
                  <Select
                    value={selectedUserRange}
                    onChange={(e) => {
                      //setSelectedCity(e.target.value);
                      setSelectedUserRange(e.target.value);
                      onCityChange?.(e.target.value);
                      setSelectedHours("");
                    }}
                    label="Select User Range"
                    MenuProps={{
                      disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                    }}
                  >
                    {availableCities
                      .filter((c) => c.toLowerCase().includes("users"))
                      .map((userTier) => (
                        <MenuItem key={userTier} value={userTier}>
                          {userTier}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                    Select City
                  </InputLabel>
                  <Select
                    value={selectedCity}
                    onChange={(e) => {
                      setSelectedCity(e.target.value);
                      onCityChange?.(e.target.value); // send selected city back to parent
                      setSelectedDays("1");
                      setSelectedHours("4");
                    }}
                    label="Select City"
                    MenuProps={{
                      disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                    }}
                  >
                    {cityOptions.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city.charAt(0).toUpperCase() + city.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grow>
            {isVirtualGame && selectedUserRange && (
              <Fade in={true}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                    Select City
                  </InputLabel>
                  <Select
                    value={selectedCity}
                    onChange={(e) => {
                      setSelectedCity(e.target.value);
                      onCityChange?.(e.target.value); // send selected city back to parent
                      // setEventTiming("");
                      // setSelectedHours("");
                      // setSelectedDays("");
                    }}
                    label="Select City"
                    MenuProps={{
                      disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                    }}
                  >
                    {cityOptions.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city.charAt(0).toUpperCase() + city.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Fade>
            )}
            {/* Hours Selection */}
            {!isVirtualGame &&
              selectedCity &&
              getFinalStatus() !== "off" &&
              products.produt_name !== "Virtual Mosaic Wall" && (
                // !(
                //   products?.status?.[
                //     selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
                //   ]?.toLowerCase() === "off"
                // )
                <Fade in={true}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                      Select Days
                    </InputLabel>
                    <Select
                      value={selectedDays}
                      onChange={(e) => {
                        setSelectedDays(Number(e.target.value));
                        //setSelectedHours("4");
                      }}
                      label="Select Days"
                      MenuProps={{
                        disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                      }}
                    >
                      {dayOptions.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day} Day{day > 1 ? "s" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Fade>
              )}
            {isVirtualGame &&
              selectedUserRange &&
              selectedCity &&
              getFinalStatus() !== "off" && (
                // !(
                //   products?.status?.[
                //     selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
                //   ]?.toLowerCase() === "off"
                // )
                <Fade in={true}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                      Select Duration
                    </InputLabel>
                    <Select
                      value={selectedDaysLabel}
                      onChange={(e) => setSelectedDaysLabel(e.target.value)}
                      label="Select Duration"
                      MenuProps={{
                        disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                      }}
                    >
                      {virtualDurations.map((duration) => (
                        <MenuItem key={duration} value={duration}>
                          {duration}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Fade>
              )}
            {/* Days Selection */}
            {!isVirtualGame &&
              selectedDays &&
              getFinalStatus() !== "off" &&
              products.produt_name !== "Virtual Mosaic Wall" && (
                // !(
                //   products?.status?.[
                //     selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
                //   ]?.toLowerCase() === "off"
                // )
                <Fade in={true}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500, color: "#D32F2F" }}>
                      Select Hours
                    </InputLabel>
                    <Select
                      value={selectedHours}
                      onChange={(e) => {
                        setSelectedHours(Number(e.target.value));
                        //if (Number(e.target.value) > 4) {
                        //}
                      }}
                      label="Select Hours"
                      MenuProps={{
                        disableScrollLock: true, // ? THIS FIXES YOUR ISSUE
                      }}
                    >
                      {hourOptions.map((hr) => (
                        <MenuItem key={hr} value={hr}>
                          {hr} Hour{hr > 1 ? "s" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Fade>
              )}

            {/* Price Display */}
            {price > 0 &&
              (!isVirtualGame || (isVirtualGame && selectedUserRange)) &&
              // !(
              //   products?.status?.[
              //     selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
              //   ]?.toLowerCase() === "off"
              // )
              getFinalStatus() !== "off" && (
                <Fade in={true}>
                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(255, 235, 238, 0.8) 0%, rgba(255, 245, 245, 0.9) 100%)",
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid rgba(255, 205, 210, 0.5)",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      {/* <DistanceChip
                        icon={<AccessTime />}
                        label={`Timing: ${eventTiming.split(" ")[0]}`}
                      /> */}
                    </Stack>

                    <Typography variant="body1" color="#D32F2F" sx={{ mb: 1 }}>
                      Your dynamic pricing for:
                    </Typography>
                    {/* <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#D32F2F' }}>
                    {selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)} �{' '}
                    {selectedDays ? `${selectedDays} Day${selectedDays > 1 ? 's' : ''}` : ''}{' '}
                    {selectedHours ? `+ ${selectedHours} Hour${selectedHours > 1 ? 's' : ''}` : ''}
                  </Typography> */}
                    {/* <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 1, color: "#D32F2F" }}
                    >
                      {`${isVirtualGame ? "" : "In"}`}{" "}
                      {isVirtualGame
                        ? selectedUserRange
                        : selectedCity.charAt(0).toUpperCase() +
                          selectedCity.slice(1)}
                      ,
                      {isVirtualGame
                        ? `${
                            selectedDaysLabel ? selectedDaysLabel : "1 day"
                          } duration`
                        : selectedDays
                        ? ` ${selectedDays} day${selectedDays > 1 ? "s" : ""}`
                        : ""} -
                      {selectedHours
                        ? ` ${selectedHours} hour${
                            selectedHours > 1 ? "s" : ""
                          } per day`
                        : ""}
                    </Typography> */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        ml: 3,
                        mb: 1.5,
                      }}
                    >
                      {!isVirtualGame ? (
                        <>
                          {selectedCity && (
                            <Typography
                              variant="body1"
                              sx={{ color: "#D32F2F" }}
                            >
                              City:{" "}
                              <Box
                                component="span"
                                sx={{ color: "#D32F2F", fontWeight: 600 }}
                              >
                                {selectedCity.charAt(0).toUpperCase() +
                                  selectedCity.slice(1)}
                              </Box>
                            </Typography>
                          )}
                          {selectedDays && (
                            <Typography
                              variant="body1"
                              sx={{ color: "#D32F2F" }}
                            >
                              Day{parseInt(selectedDays) > 1 ? "s" : ""}:{" "}
                              <Box
                                component="span"
                                sx={{ color: "#D32F2F", fontWeight: 600 }}
                              >
                                {selectedDays}
                              </Box>
                            </Typography>
                          )}
                          {selectedHours && (
                            <Typography
                              variant="body1"
                              sx={{ color: "#D32F2F" }}
                            >
                              Hour{selectedHours > 1 ? "s" : ""} per day:{" "}
                              <Box
                                component="span"
                                sx={{ color: "#D32F2F", fontWeight: 600 }}
                              >
                                {selectedHours}
                              </Box>
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography variant="body1" sx={{ color: "#D32F2F" }}>
                            This experience is for{" "}
                            <Box component="span" sx={{ color: "#D32F2F" }}>
                              {selectedUserRange}
                            </Box>
                            {selectedDaysLabel && (
                              <>
                                , lasting{" "}
                                <Box component="span" sx={{ color: "#D32F2F" }}>
                                  {selectedDaysLabel}
                                </Box>
                              </>
                            )}
                            {selectedHours && (
                              <>
                                , with{" "}
                                <Box component="span" sx={{ color: "#D32F2F" }}>
                                  {selectedHours} hour
                                  {selectedHours > 1 ? "s" : ""}
                                </Box>{" "}
                                per day.
                              </>
                            )}
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* <Typography
  variant="h6"
  sx={{ fontWeight: 600, mb: 1, color: "#D32F2F" }}
>
  {isVirtualGame ? (
    <>
      This experience is for {selectedUserRange}
      {selectedDaysLabel && `, lasting ${selectedDaysLabel}`}
      {selectedHours && `, with ${selectedHours} hour${selectedHours > 1 ? "s" : ""} per day.`}
    </>
  ) : (
    <>
   <Box>
  {selectedCity && (
    <Typography variant="body1">
      City: {selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)}
    </Typography>
  )}

  {selectedDays && (
    <Typography variant="body1">
      Days: {selectedDays}
    </Typography>
  )}

  {selectedHours && (
    <Typography variant="body1">
      Hours per day: {selectedHours}
    </Typography>
  )}
</Box>
      {/* Happening in{" "}
      {selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)}
      {selectedDays && ` for ${selectedDays} day${selectedDays > 1 ? "s" : ""}`}
      {selectedHours && `, with ${selectedHours} hour${selectedHours > 1 ? "s" : ""} per day.`} */}
                    {/* </>
  )}
</Typography> */}

                    <Divider
                      sx={{ my: 1, borderColor: "rgba(211, 47, 47, 0.2)" }}
                    />

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        background:
                          "linear-gradient(45deg, #FF5252 0%, #D32F2F 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        display: "inline-flex",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <CurrencyRupeeIcon color="error" fontSize="large" />
                      {price.toLocaleString("en-IN")}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "#a42020ff", opacity: 0.8 }}
                    >
                      Pricing varies based on event timing and distance
                    </Typography>

                    {/* <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "#a42020ff", opacity: 0.8 }}
                    >
                      The price will automatically adjust according to the
                      distance and time
                    </Typography> */}
                  </Box>
                </Fade>
              )}
          </Stack>
        </RedGlassCard>
      </Slide>
      {/* Book Now Button */}
      {price > 0 && showDropdown && (
        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={
              // products?.status?.[
              //   selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)
              // ]?.toLowerCase() === "off"
              getFinalStatus() === "off" ? true : false
            }
            //href={"https://www.bilimbe.in/"}
            //target="_blank"
            // onClick={() => setBookingFormOpen(true)} // Update with your actual URL
            onClick={() => {
              handleAddToCart();
            }}
            sx={{
              py: 1.5,
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              "&:hover": {
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {/* Book Now */}
            Add To Cart
          </Button>
        </Box>
      )}
      {/* Success Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent
          sx={{
            textAlign: "center",
            p: 4,
            minWidth: 300,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 60, color: "green", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Item Added to Cart
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can continue shopping or view your cart.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Continue Shopping
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
            onClick={() => {
              setOpen(false);
              //setShowCart2(true)
              // redirect to cart page
              navigate("/user-dashboard/cart");
            }}
          >
            View Cart
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default PricingDetails;
